/**
 * Display labels for US IANA time zones (all states + common territories).
 * Used when showing provider slot times to clients.
 */

const US_TIMEZONE_LABELS: Record<string, string> = {
  // Eastern
  'America/New_York': 'Eastern Time (ET)',
  'America/Detroit': 'Eastern Time (ET)',
  'America/Indiana/Indianapolis': 'Eastern Time (ET)',
  'America/Indiana/Marengo': 'Eastern Time (ET)',
  'America/Indiana/Petersburg': 'Eastern Time (ET)',
  'America/Indiana/Vevay': 'Eastern Time (ET)',
  'America/Indiana/Vincennes': 'Eastern Time (ET)',
  'America/Indiana/Winamac': 'Eastern Time (ET)',
  'America/Kentucky/Louisville': 'Eastern Time (ET)',
  'America/Kentucky/Monticello': 'Eastern Time (ET)',
  'America/Louisville': 'Eastern Time (ET)',

  // Central
  'America/Chicago': 'Central Time (CT)',
  'America/Indiana/Knox': 'Central Time (CT)',
  'America/Indiana/Tell_City': 'Central Time (CT)',
  'America/Menominee': 'Central Time (CT)',
  'America/North_Dakota/Beulah': 'Central Time (CT)',
  'America/North_Dakota/Center': 'Central Time (CT)',
  'America/North_Dakota/New_Salem': 'Central Time (CT)',

  // Mountain
  'America/Denver': 'Mountain Time (MT)',
  'America/Boise': 'Mountain Time (MT)',
  'America/Phoenix': 'Arizona Time (MST)',

  // Pacific
  'America/Los_Angeles': 'Pacific Time (PT)',

  // Alaska
  'America/Anchorage': 'Alaska Time (AKT)',
  'America/Juneau': 'Alaska Time (AKT)',
  'America/Sitka': 'Alaska Time (AKT)',
  'America/Metlakatla': 'Alaska Time (AKT)',
  'America/Nome': 'Alaska Time (AKT)',
  'America/Yakutat': 'Alaska Time (AKT)',

  // Hawaii & Aleutian
  'Pacific/Honolulu': 'Hawaii Time (HST)',
  'America/Adak': 'Hawaii-Aleutian Time (HST)',

  // US territories (optional business locations)
  'America/Puerto_Rico': 'Atlantic Time (AST)',
  'America/St_Thomas': 'Atlantic Time (AST)',
  'Pacific/Guam': 'Chamorro Time (ChST)',
  'Pacific/Pago_Pago': 'Samoa Time (SST)',
};

/** Human-readable label for a US IANA time zone id. */
export function formatUsTimeZoneLabel(iana: string): string {
  const trimmed = iana.trim();
  const known = US_TIMEZONE_LABELS[trimmed];
  if (known) return known;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (name) return name;
  } catch {
    // invalid or non-IANA id
  }

  return trimmed.replace(/_/g, ' ');
}

/** Short US zone abbreviation (PT, MT, CT, ET, …) for compact UI. */
export function formatUsTimeZoneAbbreviation(iana: string): string {
  const label = formatUsTimeZoneLabel(iana);
  const parenthetical = label.match(/\(([^)]+)\)\s*$/)?.[1];
  if (parenthetical) return parenthetical;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana.trim(),
      timeZoneName: 'shortGeneric',
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (name) return name;
  } catch {
    // invalid or non-IANA id
  }

  return label;
}
